import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import api from "@/lib/api";
import { formatDistanceToNow } from "date-fns";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const fetchData = async () => {
    try {
      const [{ data: notifs }, { data: count }] = await Promise.all([
        api.get("/notifications"),
        api.get("/notifications/unread-count"),
      ]);
      setNotifications(notifs);
      setUnread(count.count);
    } catch {}
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 20000);
    return () => clearInterval(interval);
  }, []);

  const markAllRead = async () => {
    await api.patch("/notifications/read-all");
    fetchData();
  };

  const openNotification = async (n) => {
    if (!n.read) {
      api.patch(`/notifications/${n.id}/read`).then(fetchData).catch(() => {});
    }
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell-btn" className="relative p-2 transition-colors hover:bg-[hsl(var(--header-fg)/0.1)]" style={{ borderRadius: "2px", color: "hsl(var(--header-fg))" }}>
          <Bell size={17} />
          {unread > 0 && (
            <span data-testid="notification-unread-badge" className="absolute -top-0.5 -right-0.5 bg-destructive text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center" style={{ borderRadius: "2px" }}>
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" style={{ borderRadius: "2px" }} data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="label-caps">Notifications</span>
          <button data-testid="mark-all-read-btn" onClick={markAllRead} className="text-xs text-primary hover:underline font-semibold">Mark all read</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {notifications.length === 0 && (
            <div className="p-4 text-sm font-mono text-muted-foreground">&gt; no notifications yet</div>
          )}
          {notifications.map((n) => (
            <div key={n.id} data-testid={`notification-item-${n.id}`} onClick={() => openNotification(n)}
              className={`px-4 py-3 border-b border-border text-sm ${!n.read ? "bg-muted/60" : ""} ${n.link ? "cursor-pointer hover:bg-muted" : ""}`}>
              <div className="font-semibold text-xs label-caps mb-1">{n.type}</div>
              <div className="text-foreground">{n.message}</div>
              <div className="text-xs text-muted-foreground mt-1">{formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}</div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
